import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Briefcase, TrendingUp, Network, Target } from 'lucide-react';
import ProgramPageLayout from '../../components/shared/ProgramPageLayout';

const CareerDevelopment: React.FC = () => {
  return (
    <ProgramPageLayout slug="career-consulting" theme="green">
      {(program) => (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Program Features</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {program.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feature}</h3>
                  <p className="text-gray-600 text-sm">
                    Professional {feature.toLowerCase()} with strategic career guidance.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Career Development Benefits */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Our Career Development Program?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3">
                <Briefcase className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Industry Analysis</h4>
                  <p className="text-gray-600 text-sm">Deep insights into market trends and industry opportunities.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Target className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Strategic Positioning</h4>
                  <p className="text-gray-600 text-sm">Position yourself strategically for career advancement and success.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <TrendingUp className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Professional Preparation</h4>
                  <p className="text-gray-600 text-sm">Comprehensive preparation for interviews, negotiations, and career transitions.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Network className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Network Building</h4>
                  <p className="text-gray-600 text-sm">Build valuable professional networks and industry connections.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Career Development Process */}
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Development Process</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-sm">1</div>
                <div>
                  <h4 className="font-semibold text-gray-900">Career Assessment</h4>
                  <p className="text-gray-600 text-sm">Comprehensive evaluation of skills, interests, and career goals.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-sm">2</div>
                <div>
                  <h4 className="font-semibold text-gray-900">Market Research</h4>
                  <p className="text-gray-600 text-sm">In-depth analysis of industry trends and opportunities.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-sm">3</div>
                <div>
                  <h4 className="font-semibold text-gray-900">Strategy Development</h4>
                  <p className="text-gray-600 text-sm">Create personalized career strategy and action plan.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-sm">4</div>
                <div>
                  <h4 className="font-semibold text-gray-900">Implementation Support</h4>
                  <p className="text-gray-600 text-sm">Ongoing guidance throughout your career advancement journey.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </ProgramPageLayout>
  );
};

export default CareerDevelopment;
